import { apiGet, apiPatch, apiPost } from '@/shared/utils/requestBuilder';

import {
  type CreateModeloPayload,
  type ListModelosParams,
  type ListModelosResult,
  type ModeloDetail,
  type ModeloImpacto,
  type ModeloRenderResult,
  type ModeloSummary,
  type ModeloVariavel,
  type ModeloVersaoSnapshot,
  type ModelosApiEnvelope,
  type PreviewModeloPayload,
  type PublishVersaoPayload,
  type UpdateModeloDadosPayload,
} from '../types/modelosDocumentoTypes';

// TODO(#897): migrar o resto do CRUD para fora do portão de workspace — sem
// escritório na sessão, tudo que ainda usa ENDPOINT responde 400.
const ENDPOINT = '/arbitral/templates';
const ENDPOINT_CATALOGO_SEM_WORKSPACE = '/admin/arbitral/templates';

async function unwrap<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => null)) as ModelosApiEnvelope<T> | null;
  if (!response.ok || !payload || payload.success === false) {
    const rawError = payload?.error;
    const errorMessage = typeof rawError === 'string' ? rawError : rawError?.message;
    const message = errorMessage || payload?.message || 'Não foi possível concluir a operação';
    throw new Error(message);
  }
  return payload.data;
}

function buildListQuery(params: ListModelosParams): Record<string, string | number | boolean> {
  const query: Record<string, string | number | boolean> = {};
  if (params.categoria) query.categoria = params.categoria;
  if (params.categorias?.length) query.categorias = params.categorias.join(',');
  if (params.nome && params.nome.trim().length > 0) query.nome = params.nome.trim();
  if (params.apenasAtivos) query.apenasAtivos = true;
  if (params.page) query.page = params.page;
  if (params.pageSize) query.pageSize = params.pageSize;
  return query;
}

export const modelosDocumentoService = {
  async listarCatalogoVariaveis(): Promise<ModeloVariavel[]> {
    const response = await apiGet(`${ENDPOINT}/variaveis`);
    const data = await unwrap<{ items: ModeloVariavel[] }>(response);
    return data.items;
  },

  async list(params: ListModelosParams): Promise<ListModelosResult> {
    const response = await apiGet(ENDPOINT_CATALOGO_SEM_WORKSPACE, buildListQuery(params));
    return unwrap<ListModelosResult>(response);
  },

  async detail(id: string): Promise<ModeloDetail> {
    const response = await apiGet(`${ENDPOINT}/${id}`);
    return unwrap<ModeloDetail>(response);
  },

  async create(payload: CreateModeloPayload): Promise<ModeloDetail> {
    const response = await apiPost(ENDPOINT, payload);
    return unwrap<ModeloDetail>(response);
  },

  async publishVersao(id: string, payload: PublishVersaoPayload): Promise<ModeloVersaoSnapshot> {
    const response = await apiPost(`${ENDPOINT}/${id}/versoes`, payload);
    return unwrap<ModeloVersaoSnapshot>(response);
  },

  async updateDados(id: string, payload: UpdateModeloDadosPayload): Promise<ModeloDetail> {
    const response = await apiPatch(`${ENDPOINT}/${id}`, payload);
    return unwrap<ModeloDetail>(response);
  },

  async setAtivo(id: string, ativo: boolean): Promise<ModeloSummary> {
    const response = await apiPatch(`${ENDPOINT}/${id}/ativo`, { ativo });
    return unwrap<ModeloSummary>(response);
  },

  async impacto(id: string): Promise<ModeloImpacto> {
    const response = await apiGet(`${ENDPOINT}/${id}/impacto`);
    return unwrap<ModeloImpacto>(response);
  },

  async preview(payload: PreviewModeloPayload): Promise<ModeloRenderResult> {
    const response = await apiPost(`${ENDPOINT}/preview`, payload);
    return unwrap<ModeloRenderResult>(response);
  },
};
